"use client"

import * as React from "react"
import { useRouter, usePathname, useParams } from "next/navigation"
import {
  LogOut,
  User,
  Sun,
  Moon,
  ChevronRight,
  PanelLeft,
  Pencil,
  Loader2,
} from "lucide-react"
import { useTheme } from "next-themes"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { useAuthStore, useCurrentUser } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { cn } from "@/lib/utils"
import { ROUTES } from "@/lib/routes"
import { ROLE_LABELS } from "@/constant/roles"

const MANUAL_BREADCRUMBS: Record<string, { label: string; href?: string }[]> = {
  // "/dashboard": [{ label: "Dashboard", href: "/dashboard" }],
  "/dashboard/quotes-history": [
    { label: "Quote History", href: "/dashboard/quotes-history" },
  ],
  "/dashboard/plans": [{ label: "Plans", href: "/dashboard/plans" }],
  "/dashboard/plans/new": [
    { label: "Plans", href: "/dashboard/plans" },
    { label: "Create Plan", href: "/dashboard/plans/new" },
  ],
  // Pricing section has no root page, so "Pricing" is unclickable
  "/dashboard/pricing/loading-config": [
    { label: "Plans", href: "/dashboard/plans" },
    { label: "Loading Config", href: "/dashboard/pricing/loading-config" },
  ],
  "/dashboard/pricing/optional-benefits": [
    { label: "Plans", href: "/dashboard/plans" },
    {
      label: "Optional Benefits",
      href: "/dashboard/pricing/optional-benefits",
    },
  ],
  "/dashboard/pricing/rates": [
    { label: "Plans", href: "/dashboard/plans" },
    { label: "Rate Tables", href: "/dashboard/pricing/rates" },
  ],
  "/dashboard/pricing/plan-tiers": [
    { label: "Plans", href: "/dashboard/plans" },
    { label: "Plan Tiers", href: "/dashboard/pricing/plan-tiers" },
  ],
  "/dashboard/pricing/fees": [
    { label: "Plans", href: "/dashboard/plans" },
    { label: "Fee Config", href: "/dashboard/pricing/fees" },
  ],
  "/dashboard/pricing/adjustments": [
    { label: "Plans", href: "/dashboard/plans" },
    { label: "Adjustments", href: "/dashboard/pricing/adjustments" },
  ],
  "/dashboard/pricing/versions": [
    { label: "Plans", href: "/dashboard/plans" },
    { label: "Version History", href: "/dashboard/pricing/versions" },
  ],
  "/dashboard/pricing/configuration": [
    { label: "Plans", href: "/dashboard/plans" },
    { label: "Configuration", href: "/dashboard/pricing/configuration" },
  ],
  // Users section
  "/dashboard/users": [{ label: "Users", href: "/dashboard/users" }],
  "/dashboard/users/roles": [
    { label: "Users", href: "/dashboard/users" },
    { label: "Roles & Permissions", href: "/dashboard/users/roles" },
  ],
}

// Fallback labels for fully dynamic matching
const BREADCRUMB_LABELS: Record<string, string> = {
  quotes: "All Quotes",
  "quotes-history": "Quote History",
  plans: "Plans",
  "plan-tiers": "Plan Tiers",
  "loading-config": "Loading Config",
  "optional-benefits": "Optional Benefits",
  rates: "Rate Tables",
  fees: "Fee Config",
  adjustments: "Adjustments",
  versions: "Version History",
  configuration: "Configuration",
  users: "Users",
  roles: "Roles & Permissions",
}

function useBreadcrumbs(pathname: string, locale: string) {
  const pathWithoutLocale = pathname.replace(`/${locale}`, "")

  // 1. Exact Manual Matches
  if (MANUAL_BREADCRUMBS[pathWithoutLocale]) {
    const trail = MANUAL_BREADCRUMBS[pathWithoutLocale]
    return trail.map((crumb, i) => ({
      label: crumb.label,
      href: crumb.href ? `/${locale}${crumb.href}` : "",
      isLast: i === trail.length - 1,
    }))
  }

  // 2. Manual Overrides for Dynamic IDs
  const quoteMatch = pathWithoutLocale.match(
    /^\/dashboard\/quotes-history\/([^\/]+)$/
  )
  if (quoteMatch) {
    return [
      {
        label: "Quote History",
        href: `/${locale}/dashboard/quotes-history`,
        isLast: false,
      },
      {
        label: "Quote Details",
        href: `/${locale}${pathWithoutLocale}`,
        isLast: true,
      },
    ]
  }

  const planMatch = pathWithoutLocale.match(/^\/dashboard\/plans\/([^\/]+)$/)
  if (planMatch && planMatch[1] !== "new") {
    return [
      { label: "Plans", href: `/${locale}/dashboard/plans`, isLast: false },
      {
        label: "Plan Details",
        href: `/${locale}${pathWithoutLocale}`,
        isLast: true,
      },
    ]
  }

  // 3. Default fallback for unknown static routes
  const allSegments = pathWithoutLocale.split("/").filter(Boolean)

  const breadcrumbs = allSegments.map((seg, i) => {
    const href = `/${locale}/${allSegments.slice(0, i + 1).join("/")}`
    const label = BREADCRUMB_LABELS[seg] ?? seg
    return { seg, label, href }
  })

  const displayBreadcrumbs = breadcrumbs.filter((b) => b.seg !== "dashboard")

  return displayBreadcrumbs.map((b, i) => ({
    label: b.label,
    href: b.href,
    isLast: i === displayBreadcrumbs.length - 1,
  }))
}

export function DashboardTopBar() {
  const router = useRouter()
  const pathname = usePathname()
  const { locale: localeParam } = useParams()
  const locale = (localeParam as string) ?? "en"
  const user = useCurrentUser()
  const logout = useAuthStore((s) => s.logout)
  const updateProfile = useAuthStore((s) => s.updateProfile)
  const { resolvedTheme, setTheme } = useTheme()
  const { roleName } = usePermissions()

  const [showEditName, setShowEditName] = React.useState(false)
  const [editName, setEditName] = React.useState("")
  const [editNameError, setEditNameError] = React.useState("")
  const [isSavingName, setIsSavingName] = React.useState(false)

  const breadcrumbs = useBreadcrumbs(pathname, locale)

  async function handleLogout() {
    await logout()
    router.push(ROUTES.userInfo(locale))
  }

  function openEditName() {
    setEditName(user?.name ?? "")
    setEditNameError("")
    setShowEditName(true)
  }

  async function handleSaveName() {
    const trimmed = editName.trim()
    if (trimmed.length < 2) {
      setEditNameError("Name must be at least 2 characters")
      return
    }
    setIsSavingName(true)
    setEditNameError("")
    try {
      await updateProfile(trimmed)
      setShowEditName(false)
    } catch {
      // toast handled in store
    } finally {
      setIsSavingName(false)
    }
  }

  return (
    <>
      <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/95 px-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
        <SidebarTrigger className="text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white">
          <PanelLeft className="h-4 w-4" />
        </SidebarTrigger>

        {/* Breadcrumb */}
        <nav className="hidden flex-1 items-center gap-1 text-caption text-slate-500 sm:flex dark:text-slate-400">
          {breadcrumbs.map((crumb, i) => (
            <span
              key={`${crumb.label}-${i}`}
              className="flex items-center gap-1"
            >
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              {crumb.isLast || !crumb.href ? (
                <span
                  className={cn(
                    crumb.isLast
                      ? "font-medium text-slate-900 dark:text-white"
                      : "text-slate-500 dark:text-slate-400"
                  )}
                >
                  {crumb.label}
                </span>
              ) : (
                <button
                  onClick={() => router.push(crumb.href)}
                  className="cursor-pointer transition-colors hover:text-slate-900 dark:hover:text-white"
                >
                  {crumb.label}
                </button>
              )}
            </span>
          ))}
        </nav>

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-2">
          {/* Theme toggle */}
          <button
            onClick={() =>
              setTheme(resolvedTheme === "dark" ? "light" : "dark")
            }
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            aria-label="Toggle theme"
          >
            {resolvedTheme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>

          {/* User pill — name hidden on mobile */}
          {user && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5 text-caption transition-colors hover:bg-slate-50 sm:px-3 dark:border-slate-700 dark:hover:bg-slate-800">
                  <User className="h-3.5 w-3.5 shrink-0 text-nova-600 dark:text-nova-400" />
                  <span className="hidden max-w-[120px] truncate font-medium text-slate-900 sm:inline dark:text-white">
                    {user.name}
                  </span>
                  <span className="rounded-full bg-nova-600/10 px-1.5 py-0.5 text-caption font-semibold text-nova-600 dark:bg-nova-600/20 dark:text-nova-400">
                    {(ROLE_LABELS as Record<string, string>)[roleName] ??
                      roleName}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-4">
                <div className="flex flex-col space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col">
                      <span className="font-semibold text-slate-900 dark:text-white">
                        {user.name}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400">
                        {user.email}
                      </span>
                    </div>
                    <button
                      onClick={openEditName}
                      title="Edit name"
                      className="mt-0.5 shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-nova-600 dark:hover:bg-slate-800 dark:hover:text-nova-400"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="h-px bg-slate-200 dark:bg-slate-800" />
                  <div className="grid grid-cols-[1fr_auto] items-center gap-2">
                    <span className="text-slate-500 dark:text-slate-400">
                      Role
                    </span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {(ROLE_LABELS as Record<string, string>)[roleName] ??
                        roleName}
                    </span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Logout — text hidden on mobile */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="h-8 gap-1.5 border-slate-200 text-slate-500 hover:text-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <Dialog
        open={showEditName}
        onOpenChange={(o) => {
          if (!o) setShowEditName(false)
        }}
      >
        <DialogContent
          className="sm:max-w-sm"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Edit Display Name</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <input
              autoFocus
              type="text"
              value={editName}
              onChange={(e) => {
                setEditName(e.target.value)
                setEditNameError("")
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveName()
              }}
              placeholder="Your full name"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-nova-600 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
            {editNameError && (
              <p className="text-sm text-red-500 dark:text-red-400">
                {editNameError}
              </p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowEditName(false)}
                className="flex-1 rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSavingName}
                onClick={handleSaveName}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-nova-600 py-2 text-sm font-semibold text-white hover:bg-nova-700 disabled:opacity-60"
              >
                {isSavingName && <Loader2 size={14} className="animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
